import { z } from "zod";
import { splitRuleSchema } from "./expense-split.js";
// 🔴 US-E20.F3-0209 (@UC9332): MỘT nguồn định nghĩa `purposeTag` (earmark) — KHÔNG khai lại pattern
// cục bộ ở đây (tránh drift 2 nguồn như bài học ledger dual-whitelist).
import { purposeTagSchema } from "./earmark.js";

/* ===========================================================================
 * US-E7.F1-0048 — Create Expense (@UC0034/@UC0037)
 * POST /v1/funds/:id/expenses — tạo khoản chi (status pending_decision).
 * CHƯA post ledger (post ⇐ 0049). Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/* ===========================================================================
 * US-E14.F5-0174-S01 — split payload (@UC9198/@UC9199)
 * Mở rộng create/edit expense: splitRule + participants[] + source (fund_flow/peer/mixed) +
 * fundPortionMinor. shareMinor KHÔNG do client gửi ở rule tự tính (server-computed qua engine F3);
 * CHỈ rule 'custom' cho phép fixedMinor (nhập tay). .strict chống over-post.
 * ========================================================================= */

/**
 * Nguồn gánh khoản chi:
 *  - fund_flow: quỹ trả 100% (KHÔNG chia — 0 participant, 0 peer-debt).
 *  - peer: một THÀNH VIÊN ứng, người khác nợ THÀNH VIÊN đó (quỹ KHÔNG chi).
 *  - mixed: một phần quỹ + một phần peer.
 *  - fund_flow_allocated (US-AIF.P2-0253 · @UC9431): 🔴 CHI TỪ QUỸ (quỹ trả 100% CASH) NHƯNG chia
 *    GÁNH NẶNG (burden) cho từng thành viên để MINH BẠCH ai gánh bao nhiêu — **KHÔNG SINH NỢ NÀO**
 *    (quỹ đã trả, không thành viên nào nợ ai). Khác peer (không rút quỹ, chủ nợ = 1 thành viên) và
 *    khác fund_flow (không có breakdown gánh nặng). Nền tảng cho các usecase EXP-F-EQUAL/RATIO/EXCLUDE.
 */
export const EXPENSE_SOURCE = {
  FUND_FLOW: "fund_flow",
  PEER: "peer",
  MIXED: "mixed",
  FUND_FLOW_ALLOCATED: "fund_flow_allocated",
} as const;
export type ExpenseSource = (typeof EXPENSE_SOURCE)[keyof typeof EXPENSE_SOURCE];
export const expenseSourceSchema = z.enum([
  EXPENSE_SOURCE.FUND_FLOW,
  EXPENSE_SOURCE.PEER,
  EXPENSE_SOURCE.MIXED,
  EXPENSE_SOURCE.FUND_FLOW_ALLOCATED,
]);

/**
 * 1 participant INPUT cho create/edit expense split. XOR chủ thể (groupMemberId | dependentId).
 * `weight` = input tính tỉ trọng (number, KHÔNG tiền). `fixedMinor` = phần cố định STRING (rule
 * fixed_plus_even / custom). `isExempt` = miễn (share 0). shareMinor STRING chỉ cho rule 'custom'
 * (nhập tay); rule tự tính → server tính, client KHÔNG gửi (bị .strict reject nếu gửi ở đây? — cho
 * phép field nhưng service reject với rule≠custom).
 */
export const expenseSplitParticipantInputSchema = z
  .object({
    groupMemberId: z.string().uuid().optional(),
    dependentId: z.string().uuid().optional(),
    weight: z.number().positive().optional(),
    fixedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    shareMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(), // CHỈ rule 'custom'
    isExempt: z.boolean().optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    const hasMember = val.groupMemberId != null;
    const hasDependent = val.dependentId != null;
    if (hasMember === hasDependent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "đúng MỘT trong groupMemberId / dependentId (XOR chủ thể)",
        path: hasMember ? ["dependentId"] : ["groupMemberId"],
      });
    }
  });
export type ExpenseSplitParticipantInput = z.infer<typeof expenseSplitParticipantInputSchema>;

/** Khối split dùng chung create/edit: rule + source + participants + fundPortionMinor (mixed). */
export const expenseSplitPayloadSchema = z
  .object({
    splitRule: splitRuleSchema,
    source: expenseSourceSchema,
    participants: z.array(expenseSplitParticipantInputSchema),
    /** Mixed: phần rút quỹ gánh (còn lại chia peer). STRING minor unit. */
    fundPortionMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    /** Opt-in chi hộ: participant KHÁC người ứng → pay_on_behalf proposed (chưa nợ ngay). */
    optInPayOnBehalf: z.boolean().optional(),
    /**
     * 🔴🔴 US-E21.F1-0213 (@UC9346) — NGƯỜI ỨNG TIỀN (chủ nợ peer-debt). KHAI TƯỜNG MINH ở đây, KHÔNG
     * suy từ người bấm duyệt. Optional: vắng ⇒ service mặc định = NGƯỜI TẠO khoản chi (an toàn).
     * CHỈ có nghĩa với source peer/mixed; fund_flow (quỹ trả 100%) ⇒ cấm khai (superRefine bên dưới).
     */
    payerMemberId: z.string().uuid().optional(),
  })
  .strict()
  .superRefine((val, ctx) => refineExpenseSplitInvariants(val, ctx));

/**
 * 🔴 US-AIF.P2-0253b (security review MEDIUM): BẤT BIẾN chia suất — TÁCH RA hàm dùng chung để MỌI biên
 * nhận payload chia suất (create `expenseSplitPayloadSchema` LẪN admin-intervene
 * `adminInterveneSplitSchema`) enforce CÙNG MỘT bộ luật từ MỘT nguồn.
 *
 * VÌ SAO: intervene phải `.omit`/`.extend` (đổi tên `splitRule`→`newSplitRule`) ⇒ buộc gọi
 * `.innerType()` ⇒ RỤNG superRefine ⇒ biên intervene mất luật (payerMemberId trên quỹ-trả-100%,
 * participants rỗng...). Trước đây an toàn chỉ nhờ HÀNH VI TÌNH CỜ (service quên map payerMemberId) —
 * brittle: ai đó "sửa" service forward field là thành bug tiền. Nay luật fail-fast 400 tại BIÊN.
 *
 * `sourceOf`/`ruleless` cho phép schema gọi truyền shape đã đổi tên field mà vẫn tái dùng luật.
 */
export function refineExpenseSplitInvariants(
  val: {
    source: string;
    participants: readonly unknown[];
    payerMemberId?: string | undefined;
    fundPortionMinor?: unknown;
  },
  ctx: z.RefinementCtx,
): void {
  {
    // peer / mixed / fund_flow_allocated ⇒ participants non-empty (phải có ai gánh phần chia / gánh nặng).
    if (
      (val.source === EXPENSE_SOURCE.PEER ||
        val.source === EXPENSE_SOURCE.MIXED ||
        val.source === EXPENSE_SOURCE.FUND_FLOW_ALLOCATED) &&
      val.participants.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "source peer/mixed/fund_flow_allocated cần ít nhất 1 participant",
        path: ["participants"],
      });
    }
    // fund_flow ⇒ participants rỗng (quỹ trả 100% — không chia). CHỈ áp cho fund_flow THUẦN — KHÔNG mở
    // rộng sang fund_flow_allocated (allocated CẦN participant để phân bổ gánh nặng minh bạch).
    if (val.source === EXPENSE_SOURCE.FUND_FLOW && val.participants.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "source fund_flow không được có participant (quỹ trả 100%)",
        path: ["participants"],
      });
    }
    // 🔴 US-E21.F1-0213 + US-AIF.P2-0253: quỹ ứng ⇒ KHÔNG có người ứng ⇒ cấm khai payerMemberId
    // (fail-loud). Áp cho CẢ fund_flow LẪN fund_flow_allocated (cả hai đều quỹ trả 100% CASH — không
    // có chủ nợ peer nào, dù allocated có breakdown gánh nặng).
    if (
      (val.source === EXPENSE_SOURCE.FUND_FLOW ||
        val.source === EXPENSE_SOURCE.FUND_FLOW_ALLOCATED) &&
      val.payerMemberId != null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "quỹ trả 100% (fund_flow/fund_flow_allocated) không có người ứng — bỏ payerMemberId",
        path: ["payerMemberId"],
      });
    }
    // 🔴 US-AIF.P2-0253: fund_flow_allocated ⇒ service TỰ ép fundPortionMinor cân bằng (Σshare = amount,
    // fundPortionMinor cột = 0). Client KHÔNG được gửi (chống nhầm lẫn / over-post) ⇒ fail-loud.
    if (
      val.source === EXPENSE_SOURCE.FUND_FLOW_ALLOCATED &&
      val.fundPortionMinor != null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "source fund_flow_allocated không nhận fundPortionMinor (service tự cân bằng)",
        path: ["fundPortionMinor"],
      });
    }
    // mixed ⇒ fundPortionMinor bắt buộc (phần quỹ gánh).
    if (val.source === EXPENSE_SOURCE.MIXED && val.fundPortionMinor == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "source mixed cần fundPortionMinor (phần quỹ gánh)",
        path: ["fundPortionMinor"],
      });
    }
  }
}
export type ExpenseSplitPayload = z.infer<typeof expenseSplitPayloadSchema>;

/**
 * Status Expense — **MỘT NGUỒN SỰ THẬT**, phải BẰNG TẬP `expenses_status_check` của DB.
 *
 * 🔴 US-E24.F7-0356-S01 (@UC9587): enum này từng có **6** giá trị trong khi CHECK của DB cho **8** —
 * `cooling`/`cancelled` sống rời ở `cooling-freeze-anomaly.ts` và KHÔNG nối vào đây. Hệ quả đo được:
 * `admin-expenses/map-expense.ts` gọi `expenseStatusSchema.parse(row.status)` (`.parse` NÉM) ⇒ một
 * khoản chi đang trong cửa sổ nguội lọt vào trang admin ⇒ **500**. Cùng lớp bug "drift 2 whitelist"
 * đã có tiền lệ ở `TX_TYPES`.
 *
 * Guard chống drift lại: `expense-status-whitelist.test.ts` **tự đọc migration SQL** (không
 * hand-list) + self-check `🔴 GATE MÙ` khi không trích được giá trị nào.
 *
 * 🔴 MỞ RỘNG NÀY LÀ ĐỂ **ĐỌC** ĐÚNG THỰC TẾ DB — KHÔNG phải để endpoint GHI trả thêm trạng thái:
 * `createExpenseResponseSchema.status` và `expense-decision.ts` giữ nguyên `z.literal`.
 */
export const EXPENSE_STATUS = {
  DRAFT: "draft",
  PENDING_DECISION: "pending_decision",
  APPROVED: "approved",
  REJECTED: "rejected",
  POSTED: "posted",
  REVERSED: "reversed",
  /** US-E19.F2-0201 (@UC9299): duyệt xong nhưng HOÃN ghi sổ tới `postAfter` — **0 bút toán**. */
  COOLING: "cooling",
  /** US-E19.F2-0201 (@UC9302): huỷ trong cửa sổ nguội ⇒ worker KHÔNG BAO GIỜ post. */
  CANCELLED: "cancelled",
} as const;

export type ExpenseStatus =
  (typeof EXPENSE_STATUS)[keyof typeof EXPENSE_STATUS];

export const expenseStatusSchema = z.enum([
  EXPENSE_STATUS.DRAFT,
  EXPENSE_STATUS.PENDING_DECISION,
  EXPENSE_STATUS.APPROVED,
  EXPENSE_STATUS.REJECTED,
  EXPENSE_STATUS.POSTED,
  EXPENSE_STATUS.REVERSED,
  EXPENSE_STATUS.COOLING,
  EXPENSE_STATUS.CANCELLED,
]);

/** Trần biên lai (ảnh) cho 1 expense. */
export const EXPENSE_RECEIPT_MAX_COUNT = 3;

/**
 * 🔴 US-E24.F7-0356-S03 (@UC9586) — MÃ LỖI ỔN ĐỊNH khi tạo khoản chi TRÙNG NỘI DUNG.
 *
 * QĐ PO 08/08/2026 (`index.md` §7 mục 2): double-tap / retry mạng ⇒ **409 fail-loud**, KHÔNG trả
 * lặng lẽ khoản chi cũ với 200 — trả khoản cũ chính là *"nuốt im lặng một khoản chi thật"*: người
 * mua nước lần hai bấm tạo, API đưa lại khoản lần một, họ tưởng đã ghi.
 *
 * 🔴 `code` là mã **ngôn-ngữ-trung-tính**; client map `code → message` (đại ý *"Đã có khoản chi
 * giống hệt. Thêm ghi chú để phân biệt."*). Backend **KHÔNG** trả câu đã dịch (§7 i18n).
 * Lời hứa "thêm ghi chú" chỉ đúng khi `description` nằm trong khoá — có test khoá điều đó.
 */
export const EXPENSE_DUPLICATE_CONTENT_CODE = "EXPENSE_DUPLICATE_CONTENT" as const;

/** Message lỗi biên (khớp mobile). */
export const EXPENSE_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên khoản chi";
export const EXPENSE_AMOUNT_POSITIVE_MESSAGE = "Số tiền phải lớn hơn 0";
/** US-E7.F1-0450 (@UC0034) — ngày chi không được ở tương lai. */
export const EXPENSE_SPENT_AT_FUTURE_MESSAGE = "Ngày chi không thể ở tương lai";
/**
 * Dung sai lệch đồng hồ client↔server khi kiểm "không ở tương lai".
 *
 * 🔴 KHÔNG phải nới luật cho tiện: người dùng chọn "hôm nay" thì client gửi đúng thời điểm bấm, và
 * đồng hồ điện thoại chạy nhanh hơn server vài giây là chuyện thường. Không có dung sai thì thao tác
 * hợp lệ nhất — ghi khoản chi vừa xảy ra — thỉnh thoảng bị 400 mà không ai hiểu vì sao.
 * 5 phút đủ rộng cho lệch đồng hồ thật, đủ hẹp để "ngày mai" vẫn bị chặn.
 */
export const EXPENSE_SPENT_AT_CLOCK_SKEW_MS = 5 * 60 * 1000;
export const EXPENSE_RECEIPT_TOO_MANY_MESSAGE =
  "Chỉ được đính tối đa 3 ảnh biên lai.";

/**
 * 🔴 US-E24.F52-0504 (@UC0034 @UC0036) — **MỘT nguồn** cho luật của nhãn ngày chi.
 *
 * Trước task này luật chỉ sống inline trong `createExpenseRequestSchema`; nay `PATCH` cũng nhận
 * `spentAt` (bù sổ xong mới phát hiện gõ nhầm ngày là chuyện thường). Chép luật sang chỗ thứ hai
 * là mở đúng khe hở của §3.8 *"reuse, không cơ chế thứ hai"*: hai bản sẽ lệch ở lần sửa đầu tiên,
 * và lúc lệch thì tạo-mới chặn được ngày mai còn sửa thì không.
 */
export const expenseSpentAtSchema = z
  .string()
  .datetime({ offset: true })
  .refine(
    (v) => Date.parse(v) <= Date.now() + EXPENSE_SPENT_AT_CLOCK_SKEW_MS,
    EXPENSE_SPENT_AT_FUTURE_MESSAGE,
  );

/**
 * Body tạo expense. amountMinor STRING minor unit (B1) — > 0. name required (>=1 sau trim).
 * Biên lai upload qua multipart (field `receipts`), KHÔNG trong JSON body này.
 */
export const createExpenseRequestSchema = z
  .object({
    name: z.string().trim().min(1, EXPENSE_NAME_REQUIRED_MESSAGE).max(200),
    amountMinor: z
      .string()
      .regex(MONEY_INTEGER_STRING_PATTERN)
      .refine((v) => BigInt(v) > 0n, EXPENSE_AMOUNT_POSITIVE_MESSAGE)
      // Trần BIGINT Postgres (chống overflow → P2020 500). > max → 400 (validate biên).
      .refine(
        (v) => BigInt(v) <= 9223372036854775807n,
        "Số tiền vượt giới hạn cho phép.",
      ),
    category: z.string().trim().max(100).optional(),
    description: z.string().trim().max(1000).optional(),
    // US-E14.F5-0174-S01: khối split optional — vắng → backward-compat E7 (quỹ trả 100%, 0 participant).
    split: expenseSplitPayloadSchema.optional(),
    // 🔴 US-E20.F3-0209-S02 (@UC9331 @UC9332) — MỤC ĐÍCH RÀNG BUỘC (earmarked fund). Optional ở
    // CONTRACT (quỹ thường không cần khai ⇒ 0 REGRESS) nhưng BẮT BUỘC-ĐIỀU-KIỆN ở SERVICE khi quỹ
    // có ràng buộc đang hiệu lực: thiếu/sai ⇒ 422 tại POST-TIME (nguồn sự thật = service, không
    // phải form). Xem apps/api/src/modules/funds/earmark-guard.service.ts.
    purposeTag: purposeTagSchema.optional(),
    /**
     * 🔴🔴 US-E23.F2-0332-S04 (@UC9523) — NGƯỜI ỨNG PHẦN QUỸ CÒN THIẾU (chủ nợ của QUỸ).
     *
     * QĐ PO 29/07: *"Quỹ không được phép âm — âm nghĩa là thiếu record cho chủ nợ."* Khi phần tiền
     * rời quỹ vượt trần khả dụng, khai người này ⇒ lúc post ghi `ADVANCE_CREDIT` (DEBIT CASH /
     * CREDIT MEMBER_PAYABLE) cho ĐÚNG phần thiếu, CÙNG transaction ⇒ CASH không âm và chủ nợ có TÊN.
     * Vắng + vượt trần ⇒ 422 `INSUFFICIENT_FUNDS` (kèm `availableMinor`/`shortfallMinor` để client
     * hỏi *"ai đã ứng phần còn lại?"*).
     *
     * ⚠️ **KHÁC `split.payerMemberId`** — đừng nhầm hai vai:
     *   · `split.payerMemberId` = chủ nợ PEER (người ↔ người), chỉ có nghĩa với source peer/mixed.
     *   · `advancedByMemberId`  = chủ nợ của QUỸ (quỹ ↔ người), có nghĩa với MỌI source — kể cả
     *     `fund_flow` (nơi `payerMemberId` bị CẤM khai vì quỹ trả 100%): quỹ vẫn có thể rỗng.
     * Đó là lý do field này nằm ở TOP-LEVEL chứ không trong khối `split`.
     *
     * Quỹ đủ tiền ⇒ field bị BỎ QUA (service không ghi ứng khống — ứng khống = đẻ nợ ma).
     */
    advancedByMemberId: z.string().uuid().optional(),
    /**
     * 🔴 US-E7.F1-0450 (@UC0034) — NGÀY SỰ VIỆC XẢY RA, khác ngày ghi sổ (`createdAt`).
     *
     * Hai thứ lệch nhau ở tình huống hằng ngày nhất: **thủ quỹ nhập bù khoản chi của tuần trước**.
     * Vắng ⇒ cột `NULL`, mang nghĩa *"không ai khai ngày"* — khác hẳn *"chi đúng hôm nay"*, nên
     * service **KHÔNG** được mặc định `now()`.
     *
     * Quá khứ hợp lệ **không giới hạn** (bù sổ ba tháng trước là chuyện có thật). Tương lai ⇒ 400:
     * client đã chặn trong bộ chọn ngày, nhưng client không bao giờ là nơi ép luật.
     *
     * ⚠️ Là **nhãn hiển thị**, KHÔNG tham gia tính số dư hay kỳ báo cáo — thời điểm ảnh hưởng sổ vẫn
     * là lúc post bút toán. Trộn hai thứ = đổi ý nghĩa của mọi báo cáo đang chạy.
     */
    spentAt: expenseSpentAtSchema.optional(),
  })
  .strict();

export type CreateExpenseRequest = z.infer<typeof createExpenseRequestSchema>;

/** Body edit split khoản chi (draft: recompute participants; posted: REVERSAL + repost). */
export const editExpenseSplitRequestSchema = expenseSplitPayloadSchema;
export type EditExpenseSplitRequest = z.infer<typeof editExpenseSplitRequestSchema>;

export const createExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    fundId: z.string().uuid(),
    name: z.string(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    category: z.string().nullable(),
    status: z.literal(EXPENSE_STATUS.PENDING_DECISION),
    /** URL biên lai (tươi) để client hiển thị; KHÔNG phải KEY lưu DB. */
    receiptUrls: z.array(z.string().url()),
    createdAt: z.string().datetime(),
  })
  .strict();

export type CreateExpenseResponse = z.infer<
  typeof createExpenseResponseSchema
>;

/* ===========================================================================
 * US-E24.F7-0356-S01 (@UC9584 @UC9585) — BỀ MẶT **ĐỌC** KHOẢN CHI.
 *
 * VÌ SAO: khoản chi ở `pending_decision`/`cooling`/đang biểu quyết là giai đoạn DUY NHẤT người trong
 * nhóm còn kịp phản đối — nhưng nó **vô hình với mọi màn client** (`/v1/expenses/{id}` chỉ có
 * DELETE+PATCH; `/v1/funds/{id}/expenses` chỉ có POST). Người dùng chỉ thấy khoản chi SAU khi tiền đã
 * ra khỏi quỹ — ngược hoàn toàn với `docs/PRD.md:25` "linh hồn của Goppy là niềm tin và sự minh bạch".
 *
 * 🔴 REDACTION = **OMIT**, KHÔNG `0`, KHÔNG `null` giả làm số. Discriminated union theo `redacted`
 * ⇒ trạng thái "đã ẩn nhưng vẫn kèm số tiền" **KHÔNG BIỂU DIỄN ĐƯỢC** (`.strict()` ⇒ 400 tại biên).
 * Quyết định show/redact ở TẦNG SERVER qua `resolveVisibility` (@goppy/domain) — **một** resolver.
 * ========================================================================= */

/** Tóm tắt phiên đồng thuận gắn khoản chi (nullable — khoản chi không mở phiên). */
export const expenseConsensusSummarySchema = z
  .object({
    consensusId: z.string().uuid(),
    deadline: z.string().datetime().nullable(),
    mode: z.string(),
    /** Phiếu của CHÍNH viewer — null = chưa bỏ phiếu. */
    myVote: z.string().nullable(),
  })
  .strict();
export type ExpenseConsensusSummary = z.infer<typeof expenseConsensusSummarySchema>;

/** Suất chia hiển thị (không phải input tính) — tiền LUÔN string minor. */
export const expenseSplitViewSchema = z
  .object({
    splitRule: z.string(),
    fundPortionMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    participants: z.array(
      z
        .object({
          groupMemberId: z.string().uuid().nullable(),
          dependentId: z.string().uuid().nullable(),
          shareMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
          isResidualHolder: z.boolean(),
        })
        .strict(),
    ),
  })
  .strict();
export type ExpenseSplitView = z.infer<typeof expenseSplitViewSchema>;

/**
 * Field LUÔN có ở cả hai nhánh — SỰ TỒN TẠI của khoản chi là minh bạch, chỉ CON SỐ mới bị ẩn.
 * `name` giữ ở nhánh redacted (không phải tiền) để FE render "có khoản chi này, bạn không xem được số".
 */
const expenseDetailBaseShape = {
  expenseId: z.string().uuid(),
  fundId: z.string().uuid(),
  name: z.string(),
  currency: z.string().length(3),
  status: expenseStatusSchema,
  createdAt: z.string().datetime(),
  visibility: z.string(),
  /** US-E19.F2-0201 (@UC9299): mốc worker auto-post ⇒ badge "Sẽ ghi sổ lúc…". NULL = không hoãn. */
  postAfter: z.string().datetime().nullable(),
  /**
   * 🔴 US-E24.F52-0504 (@UC0036) — NGÀY SỰ VIỆC (nhãn hiển thị), khác `createdAt` (ngày ghi sổ).
   *
   * Trước task này cột tồn tại, form ghi được, mà **không màn nào đọc lại được** — người dùng nhập
   * ngày chi rồi nó biến mất. `null` = *"không ai khai ngày"*, **khác** *"chi hôm nay"*: service
   * KHÔNG mặc định `now()`, và client cũng không được tự lấp bằng `createdAt`.
   *
   * Ở phần DÙNG CHUNG (cả nhánh ẩn): đây là một NGÀY, cùng hạng với `createdAt` — không phải tiền,
   * không phải ghi chú nhạy cảm. Giấu số tiền của một khoản chi không có nghĩa là giấu chuyện nó
   * xảy ra hôm nào.
   */
  spentAt: z.string().datetime().nullable(),
  consensus: expenseConsensusSummarySchema.nullable(),
} as const;

/**
 * 🔴 REVIEW PR-7 — M4: CON TRỎ TỚI NGƯỜI chỉ tồn tại ở nhánh SHOW.
 *
 * `payerMemberId` / `advancedByMemberId` trỏ đích danh **ai đã bỏ tiền ra** cho khoản chi. Để chúng
 * ở phần dùng chung nghĩa là người không được xem khoản chi nhạy cảm vẫn đọc được *ai ứng tiền* —
 * bêu tên gián tiếp vẫn là bêu tên, và §3.8 nói thứ bị ẩn **không rời API** (luật viết cho tiền,
 * nhưng lý lẽ y hệt cho danh tính).
 */
const expensePayerPointerShape = {
  payerMemberId: z.string().uuid().nullable(),
  advancedByMemberId: z.string().uuid().nullable(),
} as const;

/** Nhánh SHOW — có tiền THẬT (string minor), mô tả, suất chia, biên lai (URL tươi có TTL). */
const shownExpenseDetailSchema = z
  .object({
    ...expenseDetailBaseShape,
    ...expensePayerPointerShape,
    redacted: z.literal(false),
    /**
     * 🔴 US-E24.F52-0504 (@UC9297) — CON TRỎ tới bút toán ĐANG HIỆU LỰC của khoản chi này.
     *
     * VÌ SAO: sửa số tiền đi qua `POST /v1/funds/:id/ledger-corrections`, mà endpoint đó nhận
     * `targetTxId` — trong khi feed và màn chi tiết chỉ có `id` của **bản ghi nguồn**. Không có
     * field này thì client phải tự dò từ dòng hoạt động sang sổ cái: mỗi client một cách dò, và
     * dò sai trên tiền là hỏng thật.
     *
     * `null` = **chưa có bút toán để sửa** — chưa post, hoặc bút toán đã bị đảo mà chưa ghi lại.
     * Sau một lượt correction, field này trỏ bút toán ĐÚNG (mới), KHÔNG phải bút toán đã bị đảo:
     * trả cái cũ là dắt người dùng thẳng vào 409 ở lần sửa kế tiếp.
     *
     * 🔴 CHỈ ở nhánh SHOW (cùng lý lẽ `expensePayerPointerShape`): người không được xem số tiền
     * cũng không cần con trỏ để đi tra số tiền đó ở bề mặt khác.
     */
    ledgerTxId: z.string().uuid().nullable(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    description: z.string().nullable(),
    split: expenseSplitViewSchema.nullable(),
    receiptUrls: z.array(z.string().url()),
  })
  .strict();

/**
 * Nhánh REDACT — **OMIT** `amountMinor`/`description`/`split`/`receiptUrls`.
 * 🔴 Số bị ẩn KHÔNG RỜI API. `.strict()` ⇒ service lỡ serialize kèm tiền là 400 fail-loud, không rò
 * lặng lẽ rồi trông chờ client tự che.
 */
const redactedExpenseDetailSchema = z
  .object({
    ...expenseDetailBaseShape,
    redacted: z.literal(true),
  })
  .strict();

export const expenseDetailResponseSchema = z.discriminatedUnion("redacted", [
  shownExpenseDetailSchema,
  redactedExpenseDetailSchema,
]);
export type ExpenseDetailResponse = z.infer<typeof expenseDetailResponseSchema>;

/**
 * Query `GET /v1/funds/:id/expenses`.
 *
 * `status` nhận 1 giá trị hoặc CSV ∈ 8 giá trị của `expenseStatusSchema`. **Vắng ⇒ TẤT CẢ** — cố ý
 * KHÔNG mặc định "đang mở": luật ẩn là thứ sau này không ai nhớ (index.md §7 mục 3).
 * 🔴 CSV có phần tử rác ⇒ **400 fail-closed**, KHÔNG lọc âm thầm (lọc âm thầm = trả tập sai mà
 * người dùng tưởng là đủ).
 */
export const listFundExpensesQuerySchema = z
  .object({
    status: z
      .string()
      .refine(
        (raw) =>
          raw
            .split(",")
            .map((s) => s.trim())
            .every((s) => expenseStatusSchema.safeParse(s).success),
        "status không hợp lệ",
      )
      .optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
  })
  .strict();
export type ListFundExpensesQuery = z.infer<typeof listFundExpensesQuerySchema>;

export const listFundExpensesResponseSchema = z
  .object({
    data: z.array(expenseDetailResponseSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
    /** true = redaction MỨC-QUỸ đã áp (mirror transactions list). Per-item vẫn theo `data[].redacted`. */
    redacted: z.boolean(),
  })
  .strict();
export type ListFundExpensesResponse = z.infer<typeof listFundExpensesResponseSchema>;
